import { getMilestoneStep } from 'Douleutaras/hooks/bids/milestoneSteps';
import { BookNowIndex, BookNowStatuses, JobPricingModels, UnFormattedMilestone } from 'Douleutaras/types';
import { useAppSelector } from 'Douleutaras/redux/hooks';
import { getRelationEntitiesFromSingleEntity } from 'Douleutaras/redux/data/jobAttributes/jobAttributesSelectors';
import { jobSelectors } from 'Douleutaras/redux/data/jobs/jobsSlice';
import { jobBidsSelectors } from 'Douleutaras/redux/data/jobBids/jobBidsSlice';
import { jobBidsBookNowSelectors } from 'Douleutaras/redux/data/jobBids/jobBidsBookNowSlice';
import { useTranslation } from 'react-i18next';
import { NegativeBookNowStatuses, PositiveBookNowStatuses } from 'Douleutaras/constants/book-now';
import {
  BidMilestoneConfiguration,
  BID_MILESTONES_ALLOW_NON_STARTED,
  BID_MILESTONES_SELECT_CONFIGURATION,
  BOOK_NOW_INDEXES, BOOK_NOW_STATUS_TO_MILESTONE_STEP, MODAL_BY_MILESTONE_POSITION
} from 'Douleutaras/constants/ccf/need-help-options';
import { bidBuyerStatusSelectors } from 'Douleutaras/redux/data/jobBids/jobBidsStatusBuyerSlice';
import {
  getMostPositiveBookNowEntryByStatus,
  getMostProgressedBookNowEntry, selectBookNowEntriesWithLargestIndex, useMostAdvancedBookNowEntry
} from 'Douleutaras/hooks/bids/bookNowEntries';

interface UseMileStonesResponse {
  statusText: BookNowStatuses;
  statusFailed: boolean;
  currentMilestoneStep: number;
  mostProgressedStatusBookEntry: BookNowIndex;
}

export function useCurrentMilestoneStep(jobId: string, bidId: string): number {
  const milestone: UnFormattedMilestone = useMostAdvancedMilestoneByJobBid(jobId, bidId);

  return milestone?.stepNumber;
}

export function useCurrentMilestoneSettings(jobId: string, bidId: string): BidMilestoneConfiguration {
  const currentMilestoneStepNumber = useCurrentMilestoneStep(jobId, bidId);
  const milestones = useMilestones(jobId);
  if (!currentMilestoneStepNumber) {
    return undefined;
  }

  // if job's milestone is greater than 4, not own bid, and milestone is positive: only allow milestones 2-4
  if (milestones.currentMilestoneStep > 4
    && milestones.currentMilestoneStep !== currentMilestoneStepNumber
    && PositiveBookNowStatuses.includes(milestones.mostProgressedStatusBookEntry?.status)
  ) {
    return BID_MILESTONES_ALLOW_NON_STARTED;
  }

  const milestoneSlug = MODAL_BY_MILESTONE_POSITION[currentMilestoneStepNumber];

  return BID_MILESTONES_SELECT_CONFIGURATION[milestoneSlug];
}

// This is wrapper to useMostAdvancedBookNowEntry. Pass it a jobId and bidId instead of bookNow ids
// same as useMilestone, but used to get most advanced milestone declared by client or pro
export function useMostAdvancedMilestoneByJobBid(jobId: string, bidId: string): UnFormattedMilestone {
  const bid = useAppSelector(state => jobBidsSelectors.selectById(state, parseInt(bidId, 0)));
  const bookNowEntryId = bid?.relationships.bookNowEntry.data?.id;
  const buyerStatusId = bid?.relationships.updateFromBuyer?.data?.id;
  const buyerStatus = useAppSelector(state => bidBuyerStatusSelectors.selectById(state, buyerStatusId));

  const milestoneStep = getMilestoneStep({
    bidBuyerEntryDate: buyerStatus?.attributes?.jobStartDate
  });

  const milestone = useMostAdvancedBookNowEntry([ bookNowEntryId ], [ buyerStatusId ])
    .pop();

  if (!milestone) {
    return;
  }

  return milestoneStep(milestone.status);
}

/**
 * By Passing the jobId, you will have access to the end status text of BookNow entries.
 * Important: Bids and BookNowEntry models should be stored inside Redux for this to
 * work (usually during fetching from api)
 */
export function useMilestones(jobId: string): UseMileStonesResponse {
  let statusText;
  let statusFailed;
  let milestoneStep;
  const { t } = useTranslation();
  const job = useAppSelector(state => jobSelectors.selectById(state, jobId));
  const selectJobBids = getRelationEntitiesFromSingleEntity(
    state => jobSelectors.selectById(state, parseInt(jobId, 0)),
    state => jobBidsSelectors.selectAll(state),
    'bids');
  const jobBids = useAppSelector(state => selectJobBids(state));
  const bookNowEntries = useAppSelector(state => jobBidsBookNowSelectors.selectAll(state));
  const bookNowEntriesIds = jobBids.map(bid => bid.relationships?.bookNowEntry?.data?.id);
  const buyerStatusIds = jobBids.map(bid => bid.relationships?.updateFromBuyer?.data?.id);
  const mostAdvancedBookNowEntry: Array<BookNowIndex> = useMostAdvancedBookNowEntry(bookNowEntriesIds, buyerStatusIds);

  // This one is strange, why do we iterate through all job bids?
  const proChoiceBookNow: Array<BookNowIndex> = jobBids
    .map(currentJobBid => {
      const bookNowEntryId = currentJobBid.relationships.bookNowEntry.data?.id;
      const bookNowEntry = bookNowEntries.find(entry => entry.id === bookNowEntryId);

      return BOOK_NOW_INDEXES.find(milestonesIndex => milestonesIndex.status === bookNowEntry?.attributes.status)
        ?? BOOK_NOW_INDEXES[0];
    });

  const mostProgressedBookNowEntries: Array<BookNowIndex> = proChoiceBookNow.length > 0
    ? selectBookNowEntriesWithLargestIndex(proChoiceBookNow, mostAdvancedBookNowEntry)
    : [];

  const mostPositiveStatusBookNowEntry: BookNowIndex  = mostProgressedBookNowEntries.length > 0 ?
    getMostPositiveBookNowEntryByStatus(mostProgressedBookNowEntries)
    : { status: undefined, index: undefined };

  const allBidStatusesAreNegative: boolean = mostProgressedBookNowEntries.length > 0 &&
    mostProgressedBookNowEntries.every(
      jobNowEntry => NegativeBookNowStatuses.includes(jobNowEntry.status)
    );

  const mostProgressedStatusBookEntry = mostProgressedBookNowEntries.length > 0
    ? getMostProgressedBookNowEntry(mostProgressedBookNowEntries)
    : { status: undefined, index: undefined };

  // Logic described https://gitlab.com/d3454/core/douleutaras/-/issues/65
  // & https://gitlab.com/d3454/core/douleutaras/-/issues/294
  switch (true) {
    case !jobId:
      statusText = undefined;
      statusFailed = undefined;
      break;
    // All id statuses are negative
    case allBidStatusesAreNegative:
      statusText = t('Something went wrong');
      statusFailed = true;
      break;

    // Show most advanced milestone status name
    // SPEC: When no BookNow Status Found (Credit model), we put default status to ACTIVE
    case Boolean(mostPositiveStatusBookNowEntry.status):
      milestoneStep = getMilestoneStep({});
      statusText = milestoneStep(mostPositiveStatusBookNowEntry.status).label;
      statusFailed = false;
      break;

    // Job is credits model and at least 1 bid exists.
    case job?.attributes.pricingModel === JobPricingModels.CREDITS_MODEL && jobBids.length > 0:
      milestoneStep = getMilestoneStep({});
      statusText = milestoneStep(BookNowStatuses.ACTIVE).label;
      break;

    // No bid received yet OR Extra bid asked and all bid statuses are negative
    case jobBids?.length < 1 || (allBidStatusesAreNegative && job.attributes.askedMoreBids):
      milestoneStep = getMilestoneStep({});
      statusText = milestoneStep(BookNowStatuses.JOB_POST).label;
      break;
    default:
      break;
  }

  return {
    statusText,
    statusFailed,
    currentMilestoneStep: BOOK_NOW_STATUS_TO_MILESTONE_STEP[mostProgressedStatusBookEntry.status],
    mostProgressedStatusBookEntry
  };
}
